import { formatLengthWithBasis } from '~/utils';
import type { BuyListDetailItem } from '~/pages/buy-lists/sections/types';
import type { BuyListDetailTally } from '~/pages/buy-lists/sections/tallies/types';

export type SelectionConflict =
  | { reason: 'multipleCannedTalliesForProduct' }
  | { reason: 'lengthInAnotherSelectedLine'; lengthLabel: string };

export type SelectionConflictsByRowId = ReadonlyMap<
  string,
  SelectionConflict[]
>;

export const NO_SELECTION_CONFLICTS: SelectionConflictsByRowId = new Map();

const NO_ROW_CONFLICTS: SelectionConflict[] = [];

export function conflictsOfRow(
  conflictsByRowId: SelectionConflictsByRowId,
  rowId: string
): SelectionConflict[] {
  return conflictsByRowId.get(rowId) ?? NO_ROW_CONFLICTS;
}

type SelectedLength = {
  id: string;
  lengthDisplayValue: string;
  isNominal?: boolean | null;
};

type AddConflict = (rowId: string, conflict: SelectionConflict) => void;

function addCannedTallyProductConflicts(
  selectedTallies: BuyListDetailTally[],
  addConflict: AddConflict
) {
  const cannedTallyIdsByProductId = new Map<string, string[]>();
  for (const tally of selectedTallies) {
    if (tally.__typename !== 'CannedBuyListTally') continue;
    const productId = tally.platformProduct.id;
    cannedTallyIdsByProductId.set(productId, [
      ...(cannedTallyIdsByProductId.get(productId) ?? []),
      tally.id,
    ]);
  }
  for (const tallyIds of cannedTallyIdsByProductId.values()) {
    if (tallyIds.length < 2) continue;
    tallyIds.forEach((tallyId) =>
      addConflict(tallyId, { reason: 'multipleCannedTalliesForProduct' })
    );
  }
}

function addSharedLengthConflicts(
  selectedItems: BuyListDetailItem[],
  selectedTallies: BuyListDetailTally[],
  addConflict: AddConflict
) {
  const rowsByLengthId = new Map<
    string,
    { lengthLabel: string; rowIds: string[] }
  >();
  const recordLength = (rowId: string, length: SelectedLength) => {
    const rows = rowsByLengthId.get(length.id);
    rowsByLengthId.set(length.id, {
      lengthLabel: formatLengthWithBasis(
        length.lengthDisplayValue,
        length.isNominal
      ),
      rowIds: rows === undefined ? [rowId] : [...rows.rowIds, rowId],
    });
  };
  selectedItems.forEach((item) =>
    recordLength(item.id, item.platformProductsLength)
  );
  selectedTallies.forEach((tally) =>
    tally.bundles.forEach((bundle) =>
      recordLength(tally.id, bundle.platformProductsLength)
    )
  );
  for (const { lengthLabel, rowIds } of rowsByLengthId.values()) {
    if (rowIds.length < 2) continue;
    rowIds.forEach((rowId) =>
      addConflict(rowId, { reason: 'lengthInAnotherSelectedLine', lengthLabel })
    );
  }
}

/** Same rules as BuyList.requireCanGoInOneCart on the backend. */
export function findSelectionConflicts(
  selectedItems: BuyListDetailItem[],
  selectedTallies: BuyListDetailTally[]
): SelectionConflictsByRowId {
  const conflictsByRowId = new Map<string, SelectionConflict[]>();
  const addConflict: AddConflict = (rowId, conflict) => {
    conflictsByRowId.set(rowId, [
      ...(conflictsByRowId.get(rowId) ?? []),
      conflict,
    ]);
  };

  addCannedTallyProductConflicts(selectedTallies, addConflict);
  addSharedLengthConflicts(selectedItems, selectedTallies, addConflict);

  return conflictsByRowId;
}
